import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  X,
  Pin,
  Sparkles,
  MessageSquare,
  Filter,
  Columns,
  Loader2,
  RefreshCw,
  Send,
  Building2,
  Users,
  TrendingUp,
  Zap,
  User,
  Copy,
  Check,
  Share2,
} from 'lucide-react';
import api from '../../utils/axios';
import PillBuilder from './PillBuilder';
import PrecisionPanel from './PrecisionPanel';
import RankingWeightControls from '../shared/RankingWeightControls';
import ListSizeControl from './ListSizeControl';
import { useSearchContext } from '../../contexts/SearchContext';
import { useSplitPane } from '../layout/SplitPaneLayout';
import { useRankingWeights } from '../../hooks/useRankingWeights';
import ExpandableInput from '../shared/ExpandableInput';

// Simple markdown renderer for Buddy responses
// DEX: Rich text rendering without external dependencies
const renderMarkdown = (text) => {
  if (!text) return null;
  
  // Split into paragraphs
  const paragraphs = text.split('\n\n');
  
  return paragraphs.map((para, pIdx) => {
    // Check for headers
    if (para.startsWith('### ')) {
      return (
        <h4 key={pIdx} className="font-semibold text-black/80 mt-3 mb-1 text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>
          {para.slice(4)}
        </h4>
      );
    }
    if (para.startsWith('## ')) {
      return (
        <h3 key={pIdx} className="font-semibold text-black/90 mt-3 mb-2 text-base" style={{ fontFamily: "'Playfair Display', serif" }}>
          {para.slice(3)}
        </h3>
      );
    }
    
    // Check for bullet lists
    if (para.includes('\n- ') || para.startsWith('- ')) {
      const items = para.split('\n').filter(line => line.startsWith('- '));
      return (
        <ul key={pIdx} className="space-y-1 my-2">
          {items.map((item, idx) => (
            <li key={idx} className="text-sm text-black/70 flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--eq-purple)] mt-1.5 flex-shrink-0" />
              <span dangerouslySetInnerHTML={{ __html: formatInlineMarkdown(item.slice(2)) }} />
            </li>
          ))}
        </ul>
      );
    }
    
    // Check for numbered lists
    if (/^\d+\.\s/.test(para)) {
      const items = para.split('\n').filter(line => /^\d+\.\s/.test(line));
      return (
        <ol key={pIdx} className="space-y-1 my-2 list-decimal list-inside">
          {items.map((item, idx) => (
            <li key={idx} className="text-sm text-black/70">
              <span dangerouslySetInnerHTML={{ __html: formatInlineMarkdown(item.replace(/^\d+\.\s/, '')) }} />
            </li>
          ))}
        </ol>
      );
    }
    
    // Regular paragraph with inline formatting
    return (
      <p key={pIdx} className="text-sm text-black/70 leading-relaxed my-1.5">
        <span dangerouslySetInnerHTML={{ __html: formatInlineMarkdown(para.replace(/\n/g, ' ')) }} />
      </p>
    );
  });
};

// Format inline markdown (bold, italic, code)
const formatInlineMarkdown = (text) => {
  if (!text) return '';
  return text
    // Bold: **text** or __text__
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-black/80">$1</strong>')
    .replace(/__(.+?)__/g, '<strong class="font-semibold text-black/80">$1</strong>')
    // Italic: *text* or _text_
    .replace(/\*(.+?)\*/g, '<em class="italic">$1</em>')
    .replace(/_(.+?)_/g, '<em class="italic">$1</em>')
    // Code: `text`
    .replace(/`(.+?)`/g, '<code class="px-1 py-0.5 bg-black/5 rounded text-xs font-mono">$1</code>');
};

/**
 * RefineCoPilot - Non-blocking refine panel with chat, learning, and suggestions
 * 
 * Replaces RefineSlideOut with embedded co-pilot experience.
 * 
 * KAI Standard: Glass Box transparency, < 2 clicks, 700ms transitions
 * DEX Standard: Premium minimalism, Playfair Display, conversational intimacy
 * ZIA Standard: Baked-in feedback loops, learning from selections
 * 
 * @param {string} query - Current search query
 * @param {string} mode - Search mode (candidate, client, company, jobs, demand)
 * @param {Array} filters - Current filters from search
 * @param {Array} outputFields - Current output fields
 * @param {Array} results - Current search results (for learning points)
 * @param {Array} checkedProfiles - Profiles user has liked/checked
 * @param {function} onUpdate - Called when applying refinement (fallback)
 * @param {function} onStreamingUpdate - Called when applying refinement (preferred)
 * @param {function} onSaveAsNew - Optional handler for saving as new search
 */
export default function RefineCoPilot({
  query: initialQuery = '',
  mode = 'people',
  filters: initialFilters = [],
  outputFields: initialOutputFields = [],
  results = [],
  checkedProfiles = [],
  onUpdate,
  onStreamingUpdate,
  onSaveAsNew,
  precisionRefinements,
  onPrecisionRefinementsChange,
}) {
  const isPeopleMode = mode === 'people';
  const { refineState, updateFilters, updateRankingWeights, updateListSize } = useSearchContext();
  const { isPinned, togglePin, closePanel } = useSplitPane();
  const { setWeights: persistRankingWeights } = useRankingWeights();
  
  // Local state
  const [activeTab, setActiveTab] = useState('filters'); // 'filters' | 'chat' | 'output'
  const [refineQuery, setRefineQuery] = useState(initialQuery);
  const [refinePillFilters, setRefinePillFilters] = useState(initialFilters);
  const [refinePillOutputFields, setRefinePillOutputFields] = useState(initialOutputFields);
  const [rankingWeights, setRankingWeights] = useState(refineState.rankingWeights || {});
  const [listSize, setListSize] = useState(refineState.listSize || 25);
  const [listMode, setListMode] = useState(refineState.listMode || 'short');
  const [isApplying, setIsApplying] = useState(false);
  
  // Buddy Chat state
  const [chatMessages, setChatMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      content: "I can **add filters**, **adjust rankings**, and **run searches** for you. Just tell me what you're looking for.",
      suggestions: [
        'Add VP & Executive filter',
        'Show me 50 results instead of 25',
        'Find biotech companies in Bay Area',
      ],
      actions: [
        { type: 'applyPreset', icon: 'filter', label: 'Add VP Filter', presetId: 'vp-exec' },
        { type: 'setListSize', icon: 'list', label: 'Show 50 Results', size: 50 },
      ],
    },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [brandVoice, setBrandVoice] = useState(null);
  const chatEndRef = useRef(null);
  
  // Fetch brand voice for context
  useEffect(() => {
    const fetchBrandVoice = async () => {
      try {
        const res = await api.get('/api/ai/brand-voice');
        if (res.data?.success && res.data.brandVoice) {
          setBrandVoice(res.data.brandVoice);
        }
      } catch (e) {
        // Silently fail - brand voice is optional
      }
    };
    fetchBrandVoice();
  }, []);
  
  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);
  
  // Cache filters - don't re-interpret on mount
  const [useCachedFilters, setUseCachedFilters] = useState(true);
  
  // Reset when initial filters change (new search loaded)
  useEffect(() => {
    setRefinePillFilters(initialFilters);
    setRefinePillOutputFields(initialOutputFields);
    setRefineQuery(initialQuery);
    setUseCachedFilters(true);
  }, [initialFilters, initialOutputFields, initialQuery]);
  
  // Extract company suggestions from results
  const companySuggestions = useMemo(() => {
    const companyMap = new Map();
    results.forEach((r) => {
      // Handle company as string or object
      let companyName = null;
      if (typeof r.companyName === 'string') {
        companyName = r.companyName;
      } else if (typeof r.company === 'string') {
        companyName = r.company;
      } else if (r.company && typeof r.company === 'object' && r.company.name) {
        companyName = r.company.name;
      } else if (r.data?.companyName) {
        companyName = r.data.companyName;
      } else if (r.data?.company) {
        companyName = typeof r.data.company === 'string' ? r.data.company : r.data.company?.name;
      }
      if (companyName && typeof companyName === 'string') {
        companyMap.set(companyName, (companyMap.get(companyName) || 0) + 1);
      }
    });
    const suggestions = Array.from(companyMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, count]) => {
        // CRITICAL: Ensure name is always a string, never an object
        let safeName = 'Unknown';
        if (typeof name === 'string' && name.length > 0) {
          safeName = name;
        } else if (name && typeof name === 'object') {
          // If name is an object, extract the name property
          if (typeof name.name === 'string' && name.name.length > 0) {
            safeName = name.name;
          } else if (name.id) {
            safeName = String(name.id);
          } else {
            safeName = 'Unknown';
          }
        } else if (name != null) {
          safeName = String(name);
        }
        return { name: safeName, count: typeof count === 'number' ? count : 0 };
      })
      .filter(s => typeof s.name === 'string' && s.name.length > 0 && s.name !== 'Unknown');
    
    // FINAL VALIDATION: Ensure every suggestion has a valid string name
    const validatedSuggestions = suggestions.map((s, idx) => {
      if (!s || typeof s !== 'object') {
        return { name: `Unknown-${idx}`, count: 0 };
      }
      let validatedName = 'Unknown';
      if (typeof s.name === 'string' && s.name.length > 0) {
        validatedName = s.name;
      } else if (s.name && typeof s.name === 'object' && typeof s.name.name === 'string') {
        validatedName = s.name.name;
      } else if (s.name != null) {
        validatedName = String(s.name);
      }
      return { name: validatedName, count: typeof s.count === 'number' ? s.count : 0 };
    }).filter(s => typeof s.name === 'string' && s.name.length > 0 && s.name !== 'Unknown');
    
    return validatedSuggestions;
  }, [results]);
  
  // Extract learning patterns from checked profiles (ZIA: min 3 for confidence)
  const learningPatterns = useMemo(() => {
    if (checkedProfiles.length < 3) return null;
    
    const titles = new Map();
    const industries = new Map();
    const sizes = new Map();
    
    checkedProfiles.forEach(p => {
      const data = p.data || p;
      if (data.jobTitle) titles.set(data.jobTitle, (titles.get(data.jobTitle) || 0) + 1);
      if (data.industry) industries.set(data.industry, (industries.get(data.industry) || 0) + 1);
      if (data.employeeRange) sizes.set(data.employeeRange, (sizes.get(data.employeeRange) || 0) + 1);
    });
    
    return {
      count: checkedProfiles.length,
      titles: Array.from(titles.entries()).sort((a,b) => b[1] - a[1]).slice(0, 3),
      industries: Array.from(industries.entries()).sort((a,b) => b[1] - a[1]).slice(0, 3),
      sizes: Array.from(sizes.entries()).sort((a,b) => b[1] - a[1]).slice(0, 2),
      confidence: checkedProfiles.length >= 5 ? 'strong' : 'emerging',
    };
  }, [checkedProfiles]);
  
  // Handle filter changes
  const handleFiltersChange = useCallback((filters, outputFields) => {
    console.log('🔄 RefineCoPilot: Filters changed', { filters, outputFields });
    setRefinePillFilters(filters);
    setRefinePillOutputFields(outputFields);
    updateFilters(filters, outputFields);
  }, [updateFilters]);
  
  // Handle ranking weight changes
  const handleRankingChange = useCallback((weights) => {
    console.log('🎚️ RefineCoPilot: Ranking weights changed', weights);
    setRankingWeights(weights);
    updateRankingWeights(weights);
    persistRankingWeights(weights);
  }, [updateRankingWeights, persistRankingWeights]);
  
  // Handle list size changes
  const handleListSizeChange = useCallback((size, mode) => {
    console.log('📏 RefineCoPilot: List size changed', { size, mode });
    setListSize(size);
    setListMode(mode);
    updateListSize(size, mode);
  }, [updateListSize]);
  
  // Build payload - use useCallback to get latest state
  const buildPayload = useCallback(() => {
    const payload = {
      query: refineQuery,
      mode,
      filters: refinePillFilters,
      outputFields: refinePillOutputFields,
      weights: rankingWeights,
      listSize,
      listMode,
    };
    console.log('📦 RefineCoPilot: Building payload', payload);
    return payload;
  }, [refineQuery, mode, refinePillFilters, refinePillOutputFields, rankingWeights, listSize, listMode]);
  
  // Handle apply refinement - ensure we capture latest state
  const handleApplyRefinement = useCallback(async () => {
    const payload = buildPayload();
    
    // Check if we have any included filters
    const hasIncludedFilters = payload.filters?.some(f => 
      f.pills?.some(p => p.status === 'included')
    );
    
    console.log('✨ RefineCoPilot: Applying refinement', {
      hasIncludedFilters,
      filtersCount: payload.filters?.length || 0,
      includedPills: payload.filters?.flatMap(f => 
        f.pills?.filter(p => p.status === 'included').map(p => p.label) || []
      ),
      mode: payload.mode,
    });
    
    if (!hasIncludedFilters) {
      console.warn('⚠️ RefineCoPilot: No included filters - search will use defaults');
    }
    
    setIsApplying(true);
    
    try {
      if (onStreamingUpdate) {
        await onStreamingUpdate(payload);
      } else if (onUpdate) {
        await onUpdate(payload);
      } else {
        console.error('❌ RefineCoPilot: No handler available (onStreamingUpdate and onUpdate are both undefined)');
      }
    } catch (error) {
      console.error('❌ RefineCoPilot: Refinement failed', error);
      // Add user-facing feedback
      alert(`Search failed: ${error.message || 'Unknown error'}. Please try again.`);
    } finally {
      setIsApplying(false);
    }
    // Don't close panel - user can continue refining
  }, [buildPayload, onStreamingUpdate, onUpdate]);
  
  // Apply learning patterns as filters
  const applyLearning = () => {
    if (!learningPatterns) return;
    
    const newFilters = [...refinePillFilters];
    
    // Add top title as filter
    if (learningPatterns.titles.length > 0) {
      const topTitle = learningPatterns.titles[0][0];
      const existingTitleFilter = newFilters.find(f => f.id === 'jobTitle');
      if (!existingTitleFilter) {
        newFilters.push({
          id: 'jobTitle',
          label: 'Job Title',
          type: 'multi-select',
          pills: [{ id: topTitle.toLowerCase().replace(/\s+/g, '-'), label: topTitle, status: 'included' }],
        });
      }
    }
    
    setRefinePillFilters(newFilters);
    handleFiltersChange(newFilters, refinePillOutputFields);
  };
  
  // =========================================================================
  // BUDDY CHAT HANDLERS
  // =========================================================================
  
  // Handle chat suggestion click
  const handleChatSuggestion = (suggestion) => {
    setChatInput(suggestion);
    // Auto-submit after brief delay for UX
    setTimeout(() => {
      setChatInput('');
      handleChatMessage(suggestion);
    }, 100);
  };
  
  // Handle chat action button click - KAI: Buddy can now EXECUTE actions, not just suggest
  const handleChatAction = (action) => {
    // Add success feedback message
    const addActionFeedback = (message) => {
      const feedbackMsg = {
        id: `feedback-${Date.now()}`,
        role: 'assistant',
        content: message,
        suggestions: [],
        actions: [],
      };
      setChatMessages(prev => [...prev, feedbackMsg]);
    };
    
    if (action.type === 'search') {
      // Trigger a search with the specified query
      window.dispatchEvent(new CustomEvent('eq:quick-search', {
        detail: { query: action.query, mode: action.mode || 'company' }
      }));
      addActionFeedback(`Searching for "${action.query}"...`);
      
    } else if (action.type === 'filter') {
      // Apply filters from the action
      if (action.filters) {
        setRefinePillFilters(action.filters);
        handleFiltersChange(action.filters, refinePillOutputFields);
        setActiveTab('filters'); // Switch to filters tab to show changes
        addActionFeedback('Filters applied. Check the Filters tab to see changes.');
      }
      
    } else if (action.type === 'addFilter') {
      // DEX: Direct filter addition - < 2 clicks to value
      const newFilters = [...refinePillFilters];
      const existingFilterIdx = newFilters.findIndex(f => f.id === action.filterId);
      const newPill = { 
        id: action.pillId || `${action.label.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`, 
        label: action.label, 
        status: action.status || 'included',
        apiValue: action.apiValue || action.label,
      };
      
      if (existingFilterIdx >= 0) {
        // Add to existing filter category
        newFilters[existingFilterIdx].pills = [
          ...newFilters[existingFilterIdx].pills.filter(p => p.label !== action.label),
          newPill
        ];
      } else {
        // Create new filter category
        newFilters.push({
          id: action.filterId || 'custom',
          label: action.filterLabel || 'Filter',
          type: 'multi-select',
          pills: [newPill],
        });
      }
      
      setRefinePillFilters(newFilters);
      handleFiltersChange(newFilters, refinePillOutputFields);
      setActiveTab('filters');
      addActionFeedback(`Added "${action.label}" filter. Click "Apply Refinement" to update results.`);
      
    } else if (action.type === 'setRanking') {
      // Adjust ranking weights
      const newWeights = { ...rankingWeights, ...action.weights };
      setRankingWeights(newWeights);
      handleRankingChange(newWeights);
      setActiveTab('filters');
      addActionFeedback(`Ranking adjusted: ${Object.entries(action.weights).map(([k, v]) => `${k}: ${v}%`).join(', ')}. Click "Apply Refinement" to re-sort.`);
      
    } else if (action.type === 'setListSize') {
      // Change list size
      const newSize = action.size || 25;
      const newMode = action.listMode || (newSize <= 25 ? 'short' : newSize <= 50 ? 'medium' : 'full');
      setListSize(newSize);
      setListMode(newMode);
      handleListSizeChange(newSize, newMode);
      setActiveTab('filters');
      addActionFeedback(`List size set to ${newSize}. Click "Apply Refinement" to update.`);
      
    } else if (action.type === 'applyPreset') {
      // Apply a quick filter preset (VP & Executives, Regulatory, etc.)
      const presets = {
        'vp-exec': { label: 'VP & Executives', title: 'VP OR "Vice President" OR Chief OR Director' },
        'regulatory': { label: 'Regulatory Affairs', title: 'Regulatory OR Compliance OR QA' },
        'commercial': { label: 'Commercial/Sales', title: 'Sales OR "Business Development" OR Commercial' },
        'engineering': { label: 'Engineering Leads', title: 'Engineering Manager OR "VP Engineering" OR CTO' },
        'hr-talent': { label: 'HR & Talent', title: 'HR OR "Human Resources" OR Talent OR Recruiter' },
        'manufacturing': { label: 'Manufacturing', title: 'Manufacturing OR Operations OR Supply Chain' },
      };
      
      const preset = presets[action.presetId];
      if (preset) {
        const newFilters = [...refinePillFilters];
        const existingTitleFilter = newFilters.findIndex(f => f.id === 'jobTitle');
        const newPill = { 
          id: action.presetId, 
          label: preset.label, 
          status: 'included',
          apiValue: preset.title 
        };
        
        if (existingTitleFilter >= 0) {
          newFilters[existingTitleFilter].pills = [
            ...newFilters[existingTitleFilter].pills.filter(p => p.id !== action.presetId),
            newPill
          ];
        } else {
          newFilters.push({
            id: 'jobTitle',
            label: 'Job Title',
            type: 'multi-select',
            pills: [newPill],
          });
        }
        
        setRefinePillFilters(newFilters);
        handleFiltersChange(newFilters, refinePillOutputFields);
        setActiveTab('filters');
        addActionFeedback(`Applied "${preset.label}" preset. Click "Apply Refinement" to search.`);
      }
      
    } else if (action.type === 'applyRefinement') {
      // Directly trigger the apply refinement action
      handleApplyRefinement();
      addActionFeedback('Applying refinement and re-searching...');
      
    } else if (action.type === 'openJobsNewTab') {
      // Open jobs search in a new tab
      const jobsUrl = action.url || `/search?mode=jobs&q=${encodeURIComponent(action.company || action.query || '')}`;
      window.open(jobsUrl, '_blank');
      addActionFeedback(`Opened jobs search in new tab for ${action.company || 'company'}.`);
    }
  };
  
  // Submit chat message
  const handleChatSubmit = () => {
    if (!chatInput.trim() || isChatLoading) return;
    handleChatMessage(chatInput.trim());
    setChatInput('');
  };
  
  // Process chat message and get response
  const handleChatMessage = async (message) => {
    // Add user message
    const userMsg = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message,
    };
    setChatMessages(prev => [...prev, userMsg]);
    setIsChatLoading(true);
    
    try {
      // Call Buddy API with brand voice context
      const response = await api.post('/api/ai/buddy-chat', {
        message,
        context: {
          currentMode: mode,
          currentQuery: refineQuery,
          brandVoice: brandVoice ? {
            valueProps: brandVoice.value_props,
            company: brandVoice.company_name,
            industry: brandVoice.custom_instructions,
          } : null,
          resultsCount: results?.length || 0,
        },
      });
      
      if (response.data?.success) {
        const assistantMsg = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: response.data.response,
          suggestions: response.data.suggestions || [],
          actions: response.data.actions || [],
        };
        setChatMessages(prev => [...prev, assistantMsg]);
      } else {
        // Fallback response
        addFallbackResponse(message);
      }
    } catch (error) {
      console.error('Buddy chat error:', error);
      addFallbackResponse(message);
    } finally {
      setIsChatLoading(false);
    }
  };
  
  // Add a smart fallback response when API fails - with ACTIONABLE buttons
  const addFallbackResponse = (message) => {
    const lowerMsg = message.toLowerCase();
    let response = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: '',
      suggestions: [],
      actions: [],
    };
    
    // Smart pattern matching for common queries - with EXECUTABLE actions
    if (lowerMsg.includes('company') || lowerMsg.includes('companies')) {
      const sizeMatch = lowerMsg.match(/(\d+)\+?\s*employees?/);
      const size = sizeMatch ? sizeMatch[1] : '500';
      response.content = `I'll search for companies with ${size}+ employees. Click below to run the search:`;
      response.actions = [
        { type: 'search', icon: 'search', label: 'Search Companies', query: message, mode: 'company' },
        { type: 'addFilter', icon: 'filter', label: `Add ${size}+ Employees`, filterId: 'employeeCount', filterLabel: 'Company Size', apiValue: `${size}+` },
      ];
      response.suggestions = [
        `Find contacts at these companies`,
        `Add industry filter`,
      ];
    } else if (lowerMsg.includes('hiring') || lowerMsg.includes('decision')) {
      response.content = `At mid-size companies (250-1000 employees), hiring decisions are typically made by:\n\n• **Director/VP level** for their department\n• **HR/Talent Acquisition** for process\n• **CEO/Founder** at smaller companies\n\nI can add these title filters for you:`;
      response.actions = [
        { type: 'applyPreset', icon: 'filter', label: 'Add VP & Executives', presetId: 'vp-exec' },
        { type: 'applyPreset', icon: 'filter', label: 'Add HR & Talent', presetId: 'hr-talent' },
      ];
      response.suggestions = [
        'Show me 50 results',
        'Search for decision makers',
      ];
    } else if (lowerMsg.includes('vp') || lowerMsg.includes('director') || lowerMsg.includes('executive')) {
      response.content = `I'll add VP & Executive level filters for you. These roles typically report to C-suite and have hiring authority.`;
      response.actions = [
        { type: 'applyPreset', icon: 'filter', label: 'Add VP Filter', presetId: 'vp-exec' },
        { type: 'search', icon: 'people', label: 'Search VPs', query: 'VP OR Director OR "Vice President"', mode: 'people' },
        { type: 'applyRefinement', icon: 'refresh', label: 'Apply & Search' },
      ];
    } else if (lowerMsg.includes('clinical') || lowerMsg.includes('regulatory') || lowerMsg.includes('quality')) {
      response.content = `Regulatory and Clinical Affairs roles are key in life sciences. I'll add the relevant filters:`;
      response.actions = [
        { type: 'applyPreset', icon: 'filter', label: 'Add Regulatory Filter', presetId: 'regulatory' },
        { type: 'search', icon: 'people', label: 'Search Clinical', query: 'Clinical Affairs OR Regulatory', mode: 'people' },
      ];
    } else if (lowerMsg.includes('signal') || lowerMsg.includes('funding') || lowerMsg.includes('fda')) {
      response.content = `Signals like funding rounds and FDA approvals indicate hiring intent. Companies that just raised typically hire 20-75+ people within 90 days.`;
      response.actions = [
        { type: 'search', icon: 'signal', label: 'Find Funding Signals', query: 'series funding round', mode: 'demand' },
        { type: 'search', icon: 'signal', label: 'Find FDA Approvals', query: 'FDA approval clearance', mode: 'demand' },
      ];
      response.suggestions = [
        'FDA approvals in Bay Area',
        'Series B biotech funding',
      ];
    } else if (lowerMsg.includes('more') || lowerMsg.includes('results') || lowerMsg.includes('50') || lowerMsg.includes('100')) {
      const sizeMatch = lowerMsg.match(/(\d+)/);
      const size = sizeMatch ? parseInt(sizeMatch[1]) : 50;
      response.content = `I'll adjust the list size to show ${size} results:`;
      response.actions = [
        { type: 'setListSize', icon: 'list', label: `Show ${size} Results`, size: size },
        { type: 'applyRefinement', icon: 'refresh', label: 'Apply & Refresh' },
      ];
    } else if (lowerMsg.includes('filter') || lowerMsg.includes('add')) {
      response.content = `I can add filters for you. Here are some common ones:`;
      response.actions = [
        { type: 'applyPreset', icon: 'filter', label: 'VP & Executives', presetId: 'vp-exec' },
        { type: 'applyPreset', icon: 'filter', label: 'Regulatory', presetId: 'regulatory' },
        { type: 'applyPreset', icon: 'filter', label: 'Commercial/Sales', presetId: 'commercial' },
      ];
      response.suggestions = [
        'Add engineering filter',
        'Add HR filter',
      ];
    } else {
      response.content = `I can help you with that. Here are quick actions I can take:`;
      response.actions = [
        { type: 'applyPreset', icon: 'filter', label: 'Add VP Filter', presetId: 'vp-exec' },
        { type: 'setListSize', icon: 'list', label: 'Show 50 Results', size: 50 },
        { type: 'search', icon: 'search', label: 'Search', query: message, mode: mode || 'people' },
      ];
      response.suggestions = [
        'Find biotech companies',
        'VP Clinical Affairs',
        'Recent FDA approvals',
      ];
    }
    
    setChatMessages(prev => [...prev, response]);
  };
  
  return (
    <div 
      className="h-full flex flex-col bg-white"
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-black/10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-black">Refine</h2>
          <div className="flex items-center gap-1">
            {/* Pin Toggle */}
            <button
              onClick={togglePin}
              className={`
                p-2 rounded-lg transition-all duration-200
                hover:scale-105 active:scale-95
                ${isPinned 
                  ? 'bg-[#5a20ff]/10 text-[#5a20ff]' 
                  : 'text-black/40 hover:text-black/60 hover:bg-black/5'
                }
              `}
              title={isPinned ? 'Unpin panel' : 'Pin panel open'}
            >
              <Pin className={`w-4 h-4 ${isPinned ? 'fill-current' : ''}`} />
            </button>
            
            {/* Close (only if not pinned) */}
            {!isPinned && (
              <button
                onClick={closePanel}
                className="p-2 text-black/40 hover:text-black/60 hover:bg-black/5 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        
        {/* Search Input - Auto-expanding for multi-line queries */}
        <div className={`
          flex items-start gap-2 bg-white border-2 rounded-xl px-3 py-2
          transition-colors duration-200
          ${refineQuery ? 'border-[#5a20ff]' : 'border-black/10'}
        `}>
          <ExpandableInput
            value={refineQuery}
            onChange={(value) => {
              setRefineQuery(value);
              setUseCachedFilters(false);
            }}
            placeholder="Describe your refinement... (e.g., 'VP of Sales in Healthcare, 500+ employees, Boston area')"
            minRows={1}
            maxRows={4}
            leftIcon={<Sparkles className="w-4 h-4 text-[#5a20ff]" />}
          />
        </div>
        
        {/* Tab Navigation - Output tab removed per user request */}
        <div className="flex items-center gap-1 mt-3">
          <TabButton
            active={activeTab === 'filters'}
            onClick={() => setActiveTab('filters')}
            icon={<Filter className="w-3.5 h-3.5" />}
            label="Filters"
          />
          <TabButton
            active={activeTab === 'chat'}
            onClick={() => setActiveTab('chat')}
            icon={<MessageSquare className="w-3.5 h-3.5" />}
            label="Chat"
          />
        </div>
      </div>
      
      {/* Body - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {activeTab === 'filters' && (
          <>
            {/* Learning Points (ZIA: show if 3+ selections) */}
            {learningPatterns && (
              <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-emerald-800">
                    Learning from {learningPatterns.count} selections
                  </p>
                  <span className={`
                    text-[10px] px-2 py-0.5 rounded-full
                    ${learningPatterns.confidence === 'strong' 
                      ? 'bg-emerald-200 text-emerald-800' 
                      : 'bg-emerald-100 text-emerald-600'
                    }
                  `}>
                    {learningPatterns.confidence === 'strong' ? 'Strong pattern' : 'Emerging'}
                  </span>
                </div>
                <div className="space-y-1 text-xs text-emerald-700">
                  {learningPatterns.titles.length > 0 && (
                    <p>Titles: {learningPatterns.titles.map(([t]) => t).join(', ')}</p>
                  )}
                  {learningPatterns.industries.length > 0 && (
                    <p>Industries: {learningPatterns.industries.map(([i]) => i).join(', ')}</p>
                  )}
                </div>
                <button 
                  onClick={applyLearning}
                  className="mt-2 text-xs text-emerald-600 hover:text-emerald-800 hover:underline transition-colors"
                >
                  Apply as filters
                </button>
              </div>
            )}
            
            {/* People mode: ContactOut PrecisionPanel for refinement */}
            {isPeopleMode ? (
              <div className="space-y-4">
                <PrecisionPanel
                  query={refineQuery}
                  mode={mode}
                  location=""
                  onRefinementsChange={onPrecisionRefinementsChange}
                  isSearching={isApplying}
                  compact={true}
                  initialRefinements={precisionRefinements}
                />
              </div>
            ) : (
              <>
                {/* Quick Filter Presets - DEX: < 2 clicks to value (non-People modes) */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-black/50">Quick Filters</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { id: 'vp-exec', label: 'VP & Executives', title: 'VP OR "Vice President" OR Chief OR Director' },
                      { id: 'regulatory', label: 'Regulatory Affairs', title: 'Regulatory OR Compliance OR QA' },
                      { id: 'commercial', label: 'Commercial/Sales', title: 'Sales OR "Business Development" OR Commercial' },
                      { id: 'engineering', label: 'Engineering Leads', title: 'Engineering Manager OR "VP Engineering" OR CTO' },
                      { id: 'hr-talent', label: 'HR & Talent', title: 'HR OR "Human Resources" OR Talent OR Recruiter' },
                      { id: 'manufacturing', label: 'Manufacturing', title: 'Manufacturing OR Operations OR Supply Chain' },
                    ].map((preset) => (
                      <button
                        key={preset.id}
                        onClick={() => {
                          const newFilters = [...refinePillFilters];
                          const existingTitleFilter = newFilters.findIndex(f => f.id === 'jobTitle');
                          const newPill = { 
                            id: preset.id, 
                            label: preset.label, 
                            status: 'included',
                            apiValue: preset.title 
                          };
                          
                          if (existingTitleFilter >= 0) {
                            newFilters[existingTitleFilter].pills = [
                              ...newFilters[existingTitleFilter].pills.filter(p => p.id !== preset.id),
                              newPill
                            ];
                          } else {
                            newFilters.push({
                              id: 'jobTitle',
                              label: 'Job Title',
                              type: 'multi-select',
                              pills: [newPill],
                            });
                          }
                          
                          setRefinePillFilters(newFilters);
                          handleFiltersChange(newFilters, refinePillOutputFields);
                        }}
                        className="px-2.5 py-1 bg-[var(--eq-purple)]/5 hover:bg-[var(--eq-purple)]/15 
                                   text-[var(--eq-purple-text)] rounded-lg text-xs font-medium 
                                   transition-all duration-200 hover:scale-105"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Company Suggestions */}
                {companySuggestions.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-black/50">Companies in results</p>
                    <div className="flex flex-wrap gap-1.5">
                      {companySuggestions
                        .filter(c => c && typeof c === 'object' && c.name)
                        .map((company, idx) => {
                        let safeName = 'Unknown';
                        if (typeof company.name === 'string' && company.name.length > 0) {
                          safeName = company.name;
                        } else if (company.name && typeof company.name === 'object' && typeof company.name.name === 'string') {
                          safeName = company.name.name;
                        } else if (company.name != null) {
                          safeName = String(company.name);
                        }
                        const safeKey = typeof safeName === 'string' && safeName.length > 0 
                          ? `company-${safeName}-${idx}` 
                          : `company-${idx}`;
                        return (
                          <CompanyPill
                            key={safeKey}
                            name={safeName}
                            count={company.count || 0}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* PillBuilder - with cached filters (non-People modes) */}
                <PillBuilder
                  key="refine-pill-builder"
                  query={refineQuery}
                  mode={mode}
                  initialFilters={refinePillFilters}
                  initialOutputFields={refinePillOutputFields}
                  onFiltersChange={handleFiltersChange}
                  showOutputFields={false}
                  compact={true}
                />
                
                <div className="space-y-4 pt-2 border-t border-black/10">
                  <RankingWeightControls
                    weights={rankingWeights}
                    onChange={handleRankingChange}
                    variant="simple"
                    showPresets={false}
                  />
                </div>
              </>
            )}
          </>
        )}
        
        {activeTab === 'chat' && (
          <div className="flex flex-col h-[calc(100vh-280px)] min-h-[400px]">
            {/* Brand Voice Context Banner */}
            {brandVoice && (
              <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[var(--eq-purple)]/5 to-transparent rounded-lg mb-3 border border-[var(--eq-purple)]/10">
                <User className="w-3.5 h-3.5 text-[var(--eq-purple)]" />
                <span className="text-xs text-[var(--eq-purple-text)] font-medium">
                  {brandVoice.value_props?.[0] || brandVoice.company_name || 'Your context loaded'}
                </span>
              </div>
            )}
            
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto space-y-4 pb-4">
              {chatMessages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div 
                    className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user' 
                        ? 'bg-[var(--eq-purple)] text-white' 
                        : 'bg-black/[0.03] border border-black/5'
                    }`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[var(--eq-purple)] to-[var(--eq-teal)] flex items-center justify-center">
                            <Sparkles className="w-3 h-3 text-white" />
                          </div>
                          <span
                            className="text-xs font-semibold text-black/50"
                            style={{ fontFamily: "'Playfair Display', serif" }}
                          >
                            Assistant
                          </span>
                        </div>
                        {/* Copy/Share buttons */}
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(msg.content);
                              // Could add toast notification here
                            }}
                            className="p-1.5 hover:bg-black/5 rounded-lg transition-colors"
                            title="Copy response"
                          >
                            <Copy className="w-3.5 h-3.5 text-black/30 hover:text-black/50" />
                          </button>
                        </div>
                      </div>
                    )}
                    {/* Rich text rendering for assistant, plain for user */}
                    {msg.role === 'user' ? (
                      <p className="text-sm leading-relaxed text-white">
                        {msg.content}
                      </p>
                    ) : (
                      <div className="prose prose-sm max-w-none">
                        {renderMarkdown(msg.content)}
                      </div>
                    )}
                    
                    {/* Suggestions */}
                    {msg.suggestions && msg.suggestions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {msg.suggestions.map((suggestion, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleChatSuggestion(suggestion)}
                            className="px-2.5 py-1 bg-white/80 hover:bg-[var(--eq-purple)]/10 border border-black/10 
                                       rounded-lg text-xs text-black/70 hover:text-[var(--eq-purple-text)] transition-all"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* Action Buttons - KAI: Buddy can now EXECUTE these actions */}
                    {msg.actions && msg.actions.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-black/5">
                        {msg.actions.map((action, idx) => {
                          // Determine button style based on action type
                          const isPrimary = action.type === 'applyRefinement' || action.type === 'search';
                          const isFilter = action.type === 'addFilter' || action.type === 'applyPreset';
                          
                          return (
                            <button
                              key={idx}
                              onClick={() => handleChatAction(action)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:scale-105 active:scale-95 ${
                                isPrimary 
                                  ? 'bg-[var(--eq-purple)] text-white hover:bg-[var(--eq-purple)]/90' 
                                  : isFilter
                                    ? 'bg-[var(--eq-teal)]/15 text-[var(--eq-teal-text)] hover:bg-[var(--eq-teal)]/25 border border-[var(--eq-teal)]/30'
                                    : 'bg-black/5 text-black/70 hover:bg-black/10'
                              }`}
                            >
                              {/* Icon based on action type or explicit icon */}
                              {(action.icon === 'search' || action.type === 'search') && <Building2 className="w-3 h-3" />}
                              {(action.icon === 'people') && <Users className="w-3 h-3" />}
                              {(action.icon === 'signal') && <TrendingUp className="w-3 h-3" />}
                              {(action.icon === 'filter' || action.type === 'addFilter' || action.type === 'applyPreset') && <Filter className="w-3 h-3" />}
                              {(action.icon === 'list' || action.type === 'setListSize') && <Columns className="w-3 h-3" />}
                              {(action.icon === 'refresh' || action.type === 'applyRefinement') && <RefreshCw className="w-3 h-3" />}
                              {(action.icon === 'ranking' || action.type === 'setRanking') && <TrendingUp className="w-3 h-3" />}
                              {action.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Loading indicator */}
              {isChatLoading && (
                <div className="flex justify-start">
                  <div className="bg-black/[0.03] border border-black/5 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-[var(--eq-purple)]" />
                      <span className="text-sm text-black/50">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={chatEndRef} />
            </div>
            
            {/* Chat Input */}
            <div className="flex-shrink-0 pt-3 border-t border-black/10">
              <div className="flex items-center gap-2 bg-white border-2 border-black/10 rounded-xl px-3 py-2 focus-within:border-[var(--eq-purple)]">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && chatInput.trim()) {
                      e.preventDefault();
                      handleChatSubmit();
                    }
                  }}
                  placeholder="Ask about companies, people, or org structure..."
                  className="flex-1 bg-transparent text-sm text-black placeholder-black/30 focus:outline-none"
                  disabled={isChatLoading}
                />
                <button
                  onClick={handleChatSubmit}
                  disabled={!chatInput.trim() || isChatLoading}
                  className="p-2 bg-[var(--eq-purple)] text-white rounded-lg hover:bg-[var(--eq-purple)]/90 
                             disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[10px] text-black/30 mt-2 text-center">
                Has access to your brand voice and search context
              </p>
            </div>
          </div>
        )}
        
        {/* Output tab removed per user request */}
      </div>
      
      {/* Footer - Apply Button */}
      <div className="flex-shrink-0 p-4 border-t border-black/10 bg-black/[0.02]">
        <div className="flex items-center gap-2">
          {onSaveAsNew && (
            <button
              onClick={() => onSaveAsNew(buildPayload())}
              className="
                flex-1 py-2.5 px-4 rounded-xl
                text-[#5a20ff] bg-[#5a20ff]/10 text-sm font-semibold
                hover:bg-[#5a20ff]/20 transition-all duration-200
                hover:scale-[1.02] active:scale-[0.98]
              "
            >
              Save as New
            </button>
          )}
          <button
            onClick={handleApplyRefinement}
            disabled={isApplying}
            className={`
              flex-1 py-2.5 px-4 rounded-xl
              bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] 
              text-white text-sm font-semibold
              transition-all duration-200
              flex items-center justify-center gap-2
              ${isApplying 
                ? 'opacity-80 cursor-wait' 
                : 'hover:opacity-90 hover:scale-[1.02] active:scale-[0.98]'
              }
            `}
          >
            {isApplying ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Re-searching...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Apply Refinement
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Tab Button
 */
function TabButton({ active, onClick, icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
        transition-all duration-200
        ${active 
          ? 'bg-[#5a20ff]/10 text-[#5a20ff]' 
          : 'text-black/50 hover:text-black/70 hover:bg-black/5'
        }
      `}
    >
      {icon}
      {label}
    </button>
  );
}

/**
 * Company Pill - for include/exclude
 */
function CompanyPill({ name, count }) {
  // Ensure name is ALWAYS a string, never an object
  let displayName = 'Unknown';
  if (typeof name === 'string' && name.length > 0) {
    displayName = name;
  } else if (name && typeof name === 'object') {
    if (typeof name.name === 'string' && name.name.length > 0) {
      displayName = name.name;
    } else {
      displayName = String(name.name || name.id || 'Unknown');
    }
  } else if (name != null) {
    displayName = String(name);
  }
  
  const [status, setStatus] = useState('suggested');
  
  const handleClick = () => {
    const next = {
      suggested: 'included',
      included: 'excluded',
      excluded: 'suggested',
    };
    setStatus(next[status]);
  };
  
  const styles = {
    suggested: 'bg-white border-black/20 text-black/60 border-dashed',
    included: 'bg-emerald-100 border-emerald-400 text-emerald-800',
    excluded: 'bg-red-50 border-red-300 text-red-600 line-through',
  };
  
  const finalDisplayName = typeof displayName === 'string' ? displayName : String(displayName || 'Unknown');
  const finalCount = typeof count === 'number' ? count : 0;
  
  return (
    <button
      onClick={handleClick}
      className={`
        inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs
        border transition-all duration-200
        hover:scale-105 active:scale-95
        ${styles[status]}
      `}
      title={`${finalDisplayName} (${finalCount} results)`}
    >
      <span className="truncate max-w-[100px]">{finalDisplayName}</span>
      <span className="text-[10px] opacity-60">{finalCount}</span>
    </button>
  );
}
